import { Injectable, OnDestroy } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { Confirmation, ConfirmationService } from "primeng/api";
import { Subject, Subscription, takeUntil } from "rxjs";

export type NhsFields = {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: Date;
};

function isStrDiff(v1: string, v2: string) {
    if (v1 == null && v2 == null) return false;
    if (v1 == null || v2 == null) return true;

    return v1.localeCompare(v2) != 0;
}

function isDoBDiff(v1: Date, v2: Date) {
    if (v1 == null && v2 == null) return false;
    if (v1 == null || v2 == null) return true;

    return v1.getDate() != v2.getDate() || v1.getMonth() != v2.getMonth() || v2.getUTCFullYear() != v2.getUTCFullYear();
}

const DefaultFieldsModifiedMessage =
    "<p>Some of your current details do not match with those existing in NHS. Is this correct?</p> " +
    "<p><em><b>Note:</b> If it is correct, please go to NHS and update your details there.</em></p>";

@Injectable({
    providedIn: "root"
})
export class NhsFieldsModifiedPromptService implements OnDestroy {
    private destroyed$ = new Subject<void>();
    private formChangeSubscription: Subscription;
    private fieldsModified = false;

    constructor(private confirmationService: ConfirmationService) {}

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    setup(form: FormGroup, originalValues: NhsFields) {
        this.fieldsModified = false;
        this.formChangeSubscription?.unsubscribe();
        if (!form || !originalValues) return;

        this.formChangeSubscription = form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((value: NhsFields) => {
            this.fieldsModified =
                (originalValues.firstName && isStrDiff(value.firstName, value.firstName)) ||
                (originalValues.lastName && isStrDiff(value.lastName, originalValues.lastName)) ||
                (originalValues.dateOfBirth && isDoBDiff(value.dateOfBirth, originalValues.dateOfBirth));
        });
    }

    prompt(continueWith?: Function, data?: { message?: string; header?: string }) {
        if (!this.fieldsModified) {
            if (continueWith) continueWith();
            return;
        }

        const confirmation = <Confirmation>{
            message: data?.message ?? DefaultFieldsModifiedMessage,
            header: data?.header ?? "NHS Data Modified",
            accept: continueWith
        };

        this.confirmationService.confirm(confirmation);
    }
}
